import * as React from 'react';
import { ServerService } from '../../ts/services/ServerService';
import { ServiceLocator } from '../../app/ServiceLocator';
import { Messages } from '../../codegen/Localize';
import { AddSelfToAlertsAction } from '../services/AddSelfToAlertsAction';
import { Set } from '../../ts/models/Set';
import { DataAlerts } from '../resources/DataAlerts';
import { IResult } from '../../ts/ServerApi/types/IResponse';
import { RemoveSelfFromAlertsAction } from '../services/RemoveSelfFromAlertsAction';
import { UserErrorCode } from '../../ts/helpers/ErrorCodes';
import { AddOrRemoveDataAlertComponent } from './AddOrRemoveFromDataAlertComponent';


export type AddOrRemoveFromDataAlertProviderProps = {
  readonly dataAlertId: string;
}

export function AddUserToDataAlertProvider(props: AddOrRemoveFromDataAlertProviderProps): JSX.Element {
  const [errorOccurred, setErrorOccurred] = React.useState(false);
  const [message, setMessage] = React.useState(undefined);
  const [dataAlertInfo, setDataAlertInfo] = React.useState(undefined);

  React.useEffect(() => {
    let isMounted = true;
    DataAlerts.getById(props.dataAlertId)
      .then(dataAlertInfo => {
        if (isMounted) {
          setDataAlertInfo(dataAlertInfo);
          if (dataAlertInfo.isCurrentUserSubscribed) {
            setMessage(Messages.addSelfToAlertsErrorAlreadyAdded({ ALERT_NAME: dataAlertInfo.name }));
          } else {
            const currentUser = ServiceLocator.get<ServerService>('ServerService').sessionUserInfo();
            DataAlerts.addUsers({ dataAlertIds: [ dataAlertInfo.id ], userIds: [ currentUser.id ] })
              .then(result => isMounted ? setStateAfterAdd(result, dataAlertInfo.name) : null)
              .catch(() => isMounted ? setStateForErrorAddingUser() : null);
          }
        }
      })
      .catch(() => isMounted ? setStateForErrorGettingAlert() : null);

    return () => isMounted = false;
  }, []);

  const setStateForErrorAddingUser = (): void => {
    setErrorOccurred(true);
    setMessage(Messages.errorUnknown());
  };

  const setStateForErrorGettingAlert = () => {
    setErrorOccurred(true);
    setMessage(Messages.errorDataAlertNotFoundNoName());
  };

  const setStateAfterAdd = (result: IResult, dataAlertName: string): void => {
    if (result.errors && result.errors.length > 0) {
      setErrorOccurred(true);
      switch (result.errors[0].code) {
        case  UserErrorCode.UserInvalidEmailAddress:
          setMessage(Messages.addSelfToAlertsErrorNoEmailAddress({ ALERT_NAME: dataAlertName }));
        default:
          setMessage(Messages.addSelfToAlertThroughEmailError({ ALERT_NAME: dataAlertName }));
      }
    } else {
      setMessage(Messages.addSelfToAlertThroughEmailSuccess({ ALERT_NAME: dataAlertName }));
    }
  };

  return <AddOrRemoveDataAlertComponent
    errorOccurred={errorOccurred}
    message={message}
    undoAction={() => RemoveSelfFromAlertsAction.removeSelfFromAlert(Set.singletonForIdItem(dataAlertInfo))}
    undoButtonText={Messages.addSelfToAlertThroughEmailRemoveMe()} />;
}

export function RemoveUserFromDataAlertProvider(props: AddOrRemoveFromDataAlertProviderProps): JSX.Element {
  const [errorOccurred, setErrorOccurred] = React.useState(false);
  const [message, setMessage] = React.useState(undefined);
  const [dataAlertInfo, setDataAlertInfo] = React.useState(undefined);

  React.useEffect(() => {
    let isMounted = true;
    DataAlerts.getById(props.dataAlertId)
      .then(dataAlertInfo => {
        if (isMounted) {
          setDataAlertInfo(dataAlertInfo);
          if (!dataAlertInfo.isCurrentUserSubscribed) {
            setMessage(Messages.removeSelfFromDataAlertsErrorAlreadyRemoved({ ALERT_NAME: dataAlertInfo.name }));
          } else {
            const currentUser = ServiceLocator.get<ServerService>('ServerService').sessionUserInfo();
            DataAlerts.removeUsers({ dataAlertIds: [ dataAlertInfo.id ], userIds: [ currentUser.id ] })
              .then(result => isMounted ? setStateAfterRemove(result, dataAlertInfo.name) : null)
              .catch(() => isMounted ? setStateForErrorRemovingUser() : null);
          }
        }
      })
      .catch(() => isMounted ? setStateForErrorGettingAlert() : null);

    return () => isMounted = false;
  }, []);

  const setStateForErrorRemovingUser = (): void => {
    setErrorOccurred(true);
    setMessage(Messages.errorUnknown());
  };

  const setStateForErrorGettingAlert = (): void => {
    setErrorOccurred(true);
    setMessage(Messages.errorDataAlertNotFoundNoName());
  };

  const setStateAfterRemove = (result: IResult, dataAlertName: string): void => {
    if (result.errors && result.errors.length > 0) {
      setErrorOccurred(true);
      setMessage(Messages.removeSelfFromDataAlertThroughEmailError({ ALERT_NAME: dataAlertName }));
    } else {
      setMessage(Messages.removeSelfFromDataAlertThroughEmailSuccess({ ALERT_NAME: dataAlertName }));
    }
  };

  return <AddOrRemoveDataAlertComponent
    errorOccurred={errorOccurred}
    message={message}
    undoAction={() => AddSelfToAlertsAction.addSelfToAlert(Set.singletonForIdItem(dataAlertInfo))}
    undoButtonText={Messages.removeSelfFromDataAlertThroughEmailAddMeBack()} />;
}
